import { Accordion, AccordionItem } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { auditApi, settingsApi, type AuditEntry } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  Alert,
  DataTable,
  DateField,
  FilterBar,
  PageHeader,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * The audit log viewer.
 *
 * Read-only, because the log is append-only: there is no endpoint to write,
 * edit or delete an entry, and the database refuses those operations outright.
 * The page says so, so a reader knows the history in front of them cannot have
 * been tidied up.
 *
 * The actor's name shown here is the one stored on the entry at the time of
 * the action, not a lookup against the user's current record. Renaming or
 * deactivating someone never rewrites who did what.
 */
export function AuditPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();

  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
    retry: false,
  });

  const actions = useQuery({
    queryKey: ['audit-actions'],
    queryFn: auditApi.actions,
    staleTime: 60 * 60 * 1000,
  });

  const entries = useQuery({
    queryKey: ['audit', { page, action, from, to }],
    queryFn: () =>
      auditApi.list({
        page,
        action: action || undefined,
        // A date input gives a plain day; the API expects an instant, so the
        // range is widened to cover the whole day in UTC.
        from: from ? new Date(`${from}T00:00:00.000Z`).toISOString() : undefined,
        to: to ? new Date(`${to}T23:59:59.999Z`).toISOString() : undefined,
      }),
  });

  const timezone = settings.data?.timezone;

  const columns: readonly DataColumn<AuditEntry>[] = [
    {
      key: 'when',
      header: t('audit.when'),
      render: (entry) => (
        <span className="whitespace-nowrap text-default-600">
          {formatDateTime(entry.occurredAt, {
            locale,
            ...(timezone ? { timezone } : {}),
          })}
        </span>
      ),
    },
    {
      key: 'who',
      header: t('audit.who'),
      render: (entry) =>
        entry.actor ? (
          <div className="flex flex-wrap items-center gap-inline">
            <span className="font-medium text-default-900">
              {entry.actor.displayName}
            </span>
            {entry.actor.role ? (
              <StatusBadge
                label={t(`roles.${entry.actor.role}`, {
                  defaultValue: entry.actor.role,
                })}
              />
            ) : null}
          </div>
        ) : (
          <span className="text-default-600">{t('audit.system')}</span>
        ),
    },
    {
      key: 'action',
      header: t('audit.action'),
      render: (entry) => (
        <span className="text-default-700">
          {t(`audit.actions.${entry.action}`, { defaultValue: entry.action })}
        </span>
      ),
    },
    {
      key: 'entity',
      header: t('audit.entity'),
      render: (entry) => (
        <code className="text-caption text-default-600">{entry.entityType}</code>
      ),
    },
    {
      key: 'changes',
      header: t('audit.changes'),
      render: (entry) => <ChangeDetails entry={entry} />,
    },
  ];

  const actionOptions = (actions.data?.actions ?? []).map((value) => ({
    value,
    label: t(`audit.actions.${value}`, { defaultValue: value }),
  }));

  return (
    <>
      <PageHeader
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
        breadcrumbs={[{ label: t('nav.auditLog') }]}
      />

      <div className="mb-gutter">
        <Alert tone="info" title={t('audit.immutableNotice')} />
      </div>

      <RegisterPanel
        toolbar={
          <FilterBar>
            <SelectField
              label={t('audit.filterAction')}
              options={actionOptions}
              value={action || null}
              onChange={(value) => {
                setAction(value ?? '');
                setPage(1);
              }}
              className="max-w-xs"
            />
            <DateField
              label={t('audit.filterFrom')}
              value={from || null}
              onChange={(value) => {
                setFrom(value ?? '');
                setPage(1);
              }}
            />
            <DateField
              label={t('audit.filterTo')}
              value={to || null}
              onChange={(value) => {
                setTo(value ?? '');
                setPage(1);
              }}
            />
          </FilterBar>
        }
      >
        <DataTable
          label={t('audit.title')}
          columns={columns}
          rows={entries.data?.data ?? []}
          rowKey={(entry) => entry.id}
          isLoading={entries.isLoading}
          error={entries.error}
          onRetry={() => void entries.refetch()}
          emptyContent={t('audit.noEntries')}
          page={page}
          totalPages={entries.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </RegisterPanel>
    </>
  );
}

/** Before and after values for one entry, collapsed until asked for. */
function ChangeDetails({ entry }: { entry: AuditEntry }) {
  const { t } = useTranslation();

  const hasChanges = entry.before !== null || entry.after !== null;
  if (!hasChanges && !entry.reason) {
    return <span className="text-default-600">—</span>;
  }

  return (
    <Accordion isCompact variant="light" className="px-0">
      <AccordionItem
        key="changes"
        aria-label={t('audit.viewChanges')}
        title={<span className="text-caption">{t('audit.viewChanges')}</span>}
      >
        <div className="flex flex-col gap-2 pb-2 text-caption">
          {entry.reason ? (
            <p className="text-default-700">
              <span className="text-default-600">{t('common.reason')}: </span>
              {entry.reason}
            </p>
          ) : null}

          {entry.before !== null ? (
            <div>
              <p className="text-default-600">{t('audit.before')}</p>
              <pre
                dir="ltr"
                className="overflow-x-auto rounded-medium bg-default-50 p-2 font-mono"
              >
                {JSON.stringify(entry.before, null, 2)}
              </pre>
            </div>
          ) : null}

          {entry.after !== null ? (
            <div>
              <p className="text-default-600">{t('audit.after')}</p>
              <pre
                dir="ltr"
                className="overflow-x-auto rounded-medium bg-default-50 p-2 font-mono"
              >
                {JSON.stringify(entry.after, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      </AccordionItem>
    </Accordion>
  );
}
